import { QuoteItem, QuoteStatus } from '../types';
import { isQuoteItemExcluded } from './quoteLogic';

/**
 * Ableitungen fuer den Kopf des Angebotseditors.
 *
 * Warum eigenes Modul: der Editor zeigte den Status bisher NEGATIV an — man
 * las ihn daran ab, welche Knoepfe fehlten. Ein Angebot, das in drei Tagen
 * ablaeuft, sah aus wie jedes andere, und ein Angebot mit dreissig noch
 * unbepreisten LV-Positionen sah aus wie ein fertiges. Beides sind Zustaende,
 * die der Nutzer sehen muss, bevor er sendet.
 *
 * Rein und ohne React, damit die Faelle (heute, morgen, abgelaufen, ohne
 * Gueltigkeitsdatum) ohne DOM-Testumgebung pruefbar sind.
 */

export const QUOTE_STATUS_META: Record<QuoteStatus, { label: string; cls: string }> = {
    draft: { label: 'Entwurf', cls: 'bg-slate-100 text-slate-600' },
    sent: { label: 'Versendet', cls: 'bg-blue-100 text-blue-700' },
    accepted: { label: 'Angenommen', cls: 'bg-emerald-100 text-emerald-700' },
    partial: { label: 'Teilbeauftragt', cls: 'bg-teal-100 text-teal-700' },
    rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
    expired: { label: 'Abgelaufen', cls: 'bg-amber-100 text-amber-700' },
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Ganze Tage von `fromIso` bis `toIso`. Negativ, wenn `toIso` zurueckliegt. */
export const daysUntil = (toIso: string, fromIso: string): number | null => {
    const a = Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`);
    const b = Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((a - b) / MS_PER_DAY);
};

export interface QuoteValidityInfo {
    text: string;
    tone: 'neutral' | 'warn' | 'danger';
}

/**
 * Restlaufzeit im Klartext. Nur wo sie etwas bedeutet: ein angenommenes oder
 * abgelehntes Angebot laeuft nicht mehr ab, und ohne Gueltigkeitsdatum gibt es
 * nichts zu melden.
 */
export const quoteValidityInfo = (
    quote: { validUntil?: string; status: QuoteStatus },
    effStatus: QuoteStatus,
    todayIso: string,
): QuoteValidityInfo | null => {
    if (effStatus === 'accepted' || effStatus === 'rejected') return null;
    if (!quote.validUntil) return null;

    const days = daysUntil(quote.validUntil, todayIso);
    if (days === null) return null;

    const date = formatDe(quote.validUntil);
    if (days < 0) {
        const n = Math.abs(days);
        return { text: `gültig bis ${date} — seit ${n} Tag${n === 1 ? '' : 'en'} abgelaufen`, tone: 'danger' };
    }
    if (days === 0) return { text: `gültig bis ${date} — läuft heute ab`, tone: 'danger' };
    if (days <= 7) return { text: `gültig bis ${date} — noch ${days} Tag${days === 1 ? '' : 'e'}`, tone: 'warn' };
    return { text: `gültig bis ${date} — noch ${days} Tage`, tone: 'neutral' };
};

const formatDe = (iso: string): string => {
    const [y, m, d] = iso.slice(0, 10).split('-');
    return y && m && d ? `${d}.${m}.${y}` : iso;
};

export interface QuoteCompleteness {
    /** Positionen, die geliefert werden (ohne gestrichene). */
    active: number;
    /** Gestrichene Positionen. */
    excluded: number;
    /** Bedarfs-/Eventualpositionen unter den aktiven. */
    nep: number;
    /**
     * Aktive Positionen ohne Einheitspreis. Der fehlende Fertigstellungs-
     * indikator: aus einer Ausschreibung uebernommene LV-Zeilen kommen
     * bewusst mit Preis 0 an, und nichts hat bisher gezaehlt, wie viele davon
     * noch offen sind.
     */
    withoutPrice: number;
    /** Aktive Positionen ohne Menge — meist eine unvollstaendig eingelesene Zeile. */
    withoutQuantity: number;
}

export const quoteCompleteness = (items: QuoteItem[]): QuoteCompleteness => {
    const all = items || [];
    const active = all.filter((i) => !isQuoteItemExcluded(i));
    return {
        active: active.length,
        excluded: all.length - active.length,
        nep: active.filter((i) => i.nep).length,
        withoutPrice: active.filter((i) => !(Number(i.unitPrice) > 0)).length,
        withoutQuantity: active.filter((i) => !(Number(i.quantity) > 0)).length,
    };
};

/**
 * Was den Versand blockieren sollte — als Text, nicht als Sperre. Der Nutzer
 * entscheidet; er soll es nur nicht uebersehen.
 */
export const quoteReadinessWarnings = (c: QuoteCompleteness): string[] => {
    const out: string[] = [];
    if (c.active === 0) out.push('Keine Position im Angebot.');
    if (c.withoutPrice > 0) {
        out.push(`${c.withoutPrice} von ${c.active} Positionen ohne Preis.`);
    }
    if (c.withoutQuantity > 0) {
        out.push(`${c.withoutQuantity} Position${c.withoutQuantity === 1 ? '' : 'en'} ohne Menge.`);
    }
    return out;
};

export interface QuoteSetupSummaryParts {
    taxRate?: number;
    validUntil?: string;
    street?: string;
    zip?: string;
    city?: string;
    /** Klartext des Begehungs-Intervalls, z. B. "14-täglich". */
    intervalText?: string;
    inspections?: number;
}

/**
 * Einzeiler fuer den eingeklappten Kopf-/Projektdatenblock.
 *
 * Diese beiden Karten tragen zusammen achtzehn Felder, von denen im Alltag
 * keines angefasst wird, sobald das Angebot einmal steht — sie standen aber
 * ueber den Positionen und schoben sie unter den Falz. Eingeklappt bleibt
 * lesbar, was darin steht.
 */
export const quoteSetupSummary = (p: QuoteSetupSummaryParts): string => {
    const parts: string[] = [];
    if (Number.isFinite(p.taxRate)) parts.push(`${p.taxRate} % USt`);
    if (p.validUntil) parts.push(`gültig bis ${formatDe(p.validUntil)}`);
    const address = [p.street, [p.zip, p.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    if (address) parts.push(address);
    // Begehungszahl VOR dem Intervalltext: die Zeile ist im Editor `truncate`,
    // und intervalSummary liefert Datumsspannen von gut 70 Zeichen je Phase.
    // Sonst faellt genau die eine Zahl weg, die man eingeklappt braucht.
    if (p.inspections && p.inspections > 0) {
        parts.push(`${p.inspections} Begehung${p.inspections === 1 ? '' : 'en'}`);
    }
    if (p.intervalText) parts.push(p.intervalText);
    return parts.join(' · ');
};
